import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { orpc } from "~/lib/orpc-client";
import { Skeleton } from "@alchema/ui";
import { EmptyState } from "~/components/shared/EmptyState";
import * as m from "~/paraglide/messages";

export const Route = createFileRoute("/analytics/resume-performance")({
  component: ResumePerformancePage,
});

function ResumeEmptyIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function TemplateEmptyIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}

function ResumePerformancePage() {
  const perfQuery = useQuery(orpc.analytics.resumePerformance.queryOptions());
  const templateQuery = useQuery(
    orpc.analytics.templateComparison.queryOptions(),
  );

  return (
    <div className="space-y-8">
      {/* Resume Performance */}
      <div>
        <p className="section-label mb-1">{m.analytics_resume_perf_title()}</p>
        <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
          {m.analytics_resume_perf_desc()}
        </p>

        {perfQuery.isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-xl" />
            ))}
          </div>
        ) : perfQuery.data && perfQuery.data.length > 0 ? (
          <div
            className="rounded-xl overflow-x-auto"
            style={{ border: "1px solid var(--border-subtle)" }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-subtle)", background: "var(--surface)" }}>
                  <th className="px-5 py-3 text-left section-label">{m.analytics_resume_perf_col_resume()}</th>
                  <th className="px-5 py-3 text-right section-label">{m.analytics_resume_perf_col_applications()}</th>
                  <th className="px-5 py-3 text-right section-label">{m.analytics_resume_perf_col_interviews()}</th>
                  <th className="px-5 py-3 text-right section-label">{m.analytics_resume_perf_col_offers()}</th>
                  <th className="px-5 py-3 text-right section-label">{m.analytics_resume_perf_col_avg_ats()}</th>
                </tr>
              </thead>
              <tbody>
                {[...perfQuery.data]
                  .sort((a, b) => b.offers - a.offers || b.interviews - a.interviews)
                  .map((row, i, arr) => {
                    const isLast = i === arr.length - 1;
                    return (
                      <tr
                        key={row.resumeId}
                        style={{
                          borderBottom: isLast ? "none" : "1px solid var(--border-subtle)",
                          background: "var(--surface)",
                        }}
                      >
                        <td className="px-5 py-3 font-medium" style={{ color: "var(--text)" }}>
                          {row.title}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>
                          {row.applications}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>
                          {row.interviews}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>
                          {row.offers}
                        </td>
                        <td className="px-5 py-3 text-right">
                          {row.avgAtsScore != null ? (
                            <AtsBadge score={row.avgAtsScore} />
                          ) : (
                            <span style={{ color: "var(--text-muted)" }}>--</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            className="rounded-xl"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
          >
            <EmptyState
              icon={<ResumeEmptyIcon />}
              title={m.analytics_resume_perf_empty()}
            />
          </div>
        )}
      </div>

      {/* Template Comparison */}
      <div>
        <p className="section-label mb-1">{m.analytics_template_title()}</p>
        <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
          {m.analytics_template_desc()}
        </p>

        {templateQuery.isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-xl" />
            ))}
          </div>
        ) : templateQuery.data && templateQuery.data.length > 0 ? (
          <div
            className="rounded-xl overflow-x-auto"
            style={{ border: "1px solid var(--border-subtle)" }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-subtle)", background: "var(--surface)" }}>
                  <th className="px-5 py-3 text-left section-label">{m.analytics_template_col_template()}</th>
                  <th className="px-5 py-3 text-right section-label">{m.analytics_template_col_applications()}</th>
                  <th className="px-5 py-3 text-right section-label">{m.analytics_template_col_responses()}</th>
                  <th className="px-5 py-3 text-right section-label">{m.analytics_template_col_interviews()}</th>
                </tr>
              </thead>
              <tbody>
                {templateQuery.data.map((row, i, arr) => {
                  const isLast = i === arr.length - 1;
                  return (
                    <tr
                      key={i}
                      style={{
                        borderBottom: isLast ? "none" : "1px solid var(--border-subtle)",
                        background: "var(--surface)",
                      }}
                    >
                      <td className="px-5 py-3 font-medium" style={{ color: "var(--text)" }}>
                        {row.template}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>
                        {row.applications}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>
                        {row.responses}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>
                        {row.interviews}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            className="rounded-xl"
            style={{ border: "1px solid var(--border-subtle)", background: "var(--surface)" }}
          >
            <EmptyState
              icon={<TemplateEmptyIcon />}
              title={m.analytics_template_empty()}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function AtsBadge({ score }: { score: number }) {
  const color =
    score >= 80
      ? "var(--success)"
      : score >= 60
        ? "var(--warning)"
        : "var(--error)";
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums"
      style={{
        color,
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
      }}
    >
      {score}
    </span>
  );
}
